import { useEffect, useState, useCallback } from 'react';
import { useSession } from '@/Recoil/Session/Session';
import { useRoles } from '@/Recoil/Topik/Roles';
import { getQuestionnaire } from '@/Data/Topik';
import { navigateTo } from '@/Utils/routing';
import { QuestionnaireResponse, QuestionGroupType } from '@/Data/Topik/types';
import EditQuestionDialog from './EditQuestionDialog';
import EditQuestionGroupDialog from './EditQuestionGroupDialog';
import './index.scss';

type TopikExamSectionPageProps = {
    examid: string;
    section: 'listening' | 'reading' | 'writing';
};

const ROLE_ORDER: Record<string, number> = {
    admin:    4,
    manager:  3,
    lecturer: 1,
    student:  1,
};

const QUESTION_ID_TO_GROUP: Record<number, string> = {
    // Listening section
    1: 'L1-3', 2: 'L1-3', 3: 'L3',
    4: 'L4-8', 5: 'L4-8', 6: 'L4-8', 7: 'L4-8', 8: 'L4-8',
    9: 'L9-12', 10: 'L9-12', 11: 'L9-12', 12: 'L9-12',
    13: 'L13-16', 14: 'L13-16', 15: 'L13-16', 16: 'L13-16',
    17: 'L17-20', 18: 'L17-20', 19: 'L17-20', 20: 'L17-20',
    21: 'L21-22', 22: 'L21-22',
    23: 'L23-24', 24: 'L23-24',
    25: 'L25-26', 26: 'L25-26',
    27: 'L27-28', 28: 'L27-28',
    29: 'L29-30', 30: 'L29-30',
    31: 'L31-32', 32: 'L31-32',
    33: 'L33-34', 34: 'L33-34',
    35: 'L35-36', 36: 'L35-36',
    37: 'L37-38', 38: 'L37-38',
    39: 'L39-40', 40: 'L39-40',
    41: 'L41-42', 42: 'L41-42',
    43: 'L43-44', 44: 'L43-44',
    45: 'L45-46', 46: 'L45-46',
    47: 'L47-48', 48: 'L47-48',
    49: 'L49-50', 50: 'L49-50',
    // Reading section
    51: 'R1-2', 52: 'R1-2',
    53: 'R3-4', 54: 'R3-4',
    55: 'R5-8', 56: 'R5-8', 57: 'R5-8', 58: 'R5-8',
    59: 'R9-12', 60: 'R9-12', 61: 'R9-12', 62: 'R9-12',
    63: 'R13-15', 64: 'R13-15', 65: 'R13-15',
    66: 'R16-18', 67: 'R16-18', 68: 'R16-18',
    69: 'R19-20', 70: 'R19-20',
    71: 'R21-22', 72: 'R21-22',
    73: 'R23-24', 74: 'R23-24',
    75: 'R25-27', 76: 'R25-27', 77: 'R25-27',
    78: 'R28-31', 79: 'R28-31', 80: 'R28-31', 81: 'R28-31',
    82: 'R32-34', 83: 'R32-34', 84: 'R32-34',
    85: 'R35-38', 86: 'R35-38', 87: 'R35-38', 88: 'R35-38',
    89: 'R39-41', 90: 'R39-41', 91: 'R39-41',
    92: 'R42-43', 93: 'R42-43',
    94: 'R44-45', 95: 'R44-45',
    96: 'R46-47', 97: 'R46-47',
    98: 'R48-50', 99: 'R48-50', 100: 'R48-50',
    // Writing section
    101: 'W1-2', 102: 'W1-2',
    103: 'W3',
    104: 'W4'
};

type QuestionType = QuestionGroupType['questions'][number];

function TopikExamSectionPage({ examid, section }: TopikExamSectionPageProps) {
    const { session } = useSession();
    const { roles, syncUserRole } = useRoles();

    const [examContent, setExamContent] = useState<QuestionnaireResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [editingQuestion, setEditingQuestion] = useState<QuestionType | null>(null);
    const [editQuestionOpen, setEditQuestionOpen] = useState(false);
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [currentGroupId, setCurrentGroupId] = useState('');

    const [editingGroup, setEditingGroup] = useState<QuestionGroupType | null>(null);
    const [editGroupOpen, setEditGroupOpen] = useState(false);

    const userRole = roles[session.email];
    const roleLevel = ROLE_ORDER[userRole?.role] ?? 0;

    useEffect(() => {
        if (!session.email) {
            navigateTo(`/login?redirectTo=/topik/exam/${examid}/${section}`);
            return;
        }
        syncUserRole({ email: session.email });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.email]);

    const fetchExamContent = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const content = await getQuestionnaire({ examid });
            if (content) {
                setExamContent(content);
            } else {
                setError('시험을 불러올 수 없습니다.');
            }
        } catch (e) {
            setError('시험을 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    }, [examid]);

    useEffect(() => {
        if (roleLevel >= ROLE_ORDER['manager']) {
            fetchExamContent();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleLevel, examid, section]);

    const getSectionQuestionGroups = useCallback((): QuestionGroupType[] => {
        if (!examContent) { return []; }
        switch (section) {
            case 'listening': return examContent.listeningQuestionGroups;
            case 'reading':   return examContent.readingQuestionGroups;
            case 'writing':   return examContent.writingQuestionGroups;
            default:          return [];
        }
    }, [examContent, section]);

    const handleAddQuestion = useCallback((groupId: string) => {
        setIsCreatingNew(true);
        setCurrentGroupId(groupId);
        setEditingQuestion(null);
        setEditQuestionOpen(true);
    }, []);

    const handleEditQuestion = useCallback((question: QuestionType) => {
        setIsCreatingNew(false);
        setCurrentGroupId('');
        setEditingQuestion(question);
        setEditQuestionOpen(true);
    }, []);

    const handleEditGroup = useCallback((group: QuestionGroupType) => {
        setEditingGroup(group);
        setEditGroupOpen(true);
    }, []);

    const sectionTitle = section.charAt(0).toUpperCase() + section.slice(1);
    const questionGroups = getSectionQuestionGroups();

    if (roleLevel < ROLE_ORDER['manager']) {
        return (
            <div className="TopikExamSectionPage">
                <div className="Container">
                    <div className="Breadcrumb">
                        <button onClick={() => navigateTo('/topik/exam')}>시험 목록</button>
                        <span>{">"}</span>
                        <button onClick={() => navigateTo(`/topik/exam/${examid}`)}>{examid}</button>
                        <span>{">"}</span>
                        <span>{sectionTitle}</span>
                    </div>
                    <div className="NoPermission">권한이 없습니다.</div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="TopikExamSectionPage">
                <div className="Container">
                    <div className="Breadcrumb">
                        <button onClick={() => navigateTo('/topik/exam')}>시험 목록</button>
                        <span>{">"}</span>
                        <button onClick={() => navigateTo(`/topik/exam/${examid}`)}>{examid}</button>
                        <span>{">"}</span>
                        <span>{sectionTitle}</span>
                    </div>
                    <div className="Loading">로딩 중...</div>
                </div>
            </div>
        );
    }

    if (error || !examContent) {
        return (
            <div className="TopikExamSectionPage">
                <div className="Container">
                    <div className="Breadcrumb">
                        <button onClick={() => navigateTo('/topik/exam')}>시험 목록</button>
                        <span>{">"}</span>
                        <button onClick={() => navigateTo(`/topik/exam/${examid}`)}>{examid}</button>
                        <span>{">"}</span>
                        <span>{sectionTitle}</span>
                    </div>
                    <div className="Error">{error || '시험을 찾을 수 없습니다.'}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="TopikExamSectionPage">
            <div className="Container">
                <div className="Breadcrumb">
                    <button onClick={() => navigateTo('/topik/exam')}>시험 목록</button>
                    <span>{">"}</span>
                    <button onClick={() => navigateTo(`/topik/exam/${examid}`)}>{examContent.exam.title}</button>
                    <span>{">"}</span>
                    <span>{sectionTitle}</span>
                </div>

                <div className="SectionHeader">
                    <h1 className="SectionTitle">{`${sectionTitle} Section`}</h1>
                    <button className="AddButton" onClick={() => handleAddQuestion('')}>
                        {"+ 문제 추가"}
                    </button>
                </div>

                {questionGroups.map((group) => (
                    <div key={group.groupid} className="QuestionGroup">
                        <div className="GroupHeader">
                            <div className="GroupInfo">
                                <h3 className="GroupId">{group.groupid}</h3>
                                {group.shared_question_text && (
                                    <p className="SharedQuestionText">{group.shared_question_text}</p>
                                )}
                                {group.shared_resource && (
                                    <span className="ResourceBadge">{`Resource: ${group.shared_resource.type}`}</span>
                                )}
                            </div>
                            <button className="EditGroupButton" onClick={() => handleEditGroup(group)}>
                                {"수정"}
                            </button>
                        </div>

                        {group.shared_resource?.type === 'audio' && group.shared_resource.audio_url && (
                            <div className="AudioPlayer">
                                <audio controls>
                                    <source src={group.shared_resource.audio_url} type="audio/mpeg" />
                                </audio>
                                {group.shared_resource.audio_script && Array.isArray(group.shared_resource.audio_script) && (
                                    <div className="AudioScript">
                                        <p className="ScriptTitle">Audio Script:</p>
                                        {group.shared_resource.audio_script.map((script: { role: string; text: string }, idx: number) => (
                                            <p key={idx} className="ScriptLine">
                                                <strong>{script.role}:</strong> {script.text}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {group.shared_resource?.type === 'text' && group.shared_resource.text && (
                            <div className="TextResource">
                                <p>{group.shared_resource.text}</p>
                            </div>
                        )}

                        <table className="QuestionsTable">
                            <thead>
                                <tr>
                                    <th className="ColNum">{"Q#"}</th>
                                    <th className="ColText">{"Text"}</th>
                                    <th className="ColType">{"Type"}</th>
                                    <th className="ColPoints">{"Points"}</th>
                                    <th className="ColDiff">{"Difficulty"}</th>
                                    <th className="ColActions">{"Actions"}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.questions.map((question) => (
                                    <tr key={question.questionid}>
                                        <td className="ColNum">{question.questionid}</td>
                                        <td className="ColText">
                                            <span className="QuestionText">{question.text || question.question_type || '-'}</span>
                                            <div className="QuestionBadges">
                                                {question.image && <span className="Badge">이미지</span>}
                                                {question.audio && <span className="Badge audio">오디오</span>}
                                            </div>
                                        </td>
                                        <td className="ColType">
                                            <span className={`TypeBadge ${question.type}`}>{question.type}</span>
                                        </td>
                                        <td className="ColPoints">{question.points}</td>
                                        <td className="ColDiff">
                                            <span className={`DiffBadge level-${question.difficulty_level}`}>
                                                {question.difficulty_level}
                                            </span>
                                        </td>
                                        <td className="ColActions">
                                            <button className="EditButton" onClick={() => handleEditQuestion(question)}>
                                                {"수정"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>

            <EditQuestionDialog
                key={`question-${editingQuestion?.questionid ?? 'new'}`}
                isOpen={editQuestionOpen}
                onClose={() => setEditQuestionOpen(false)}
                question={editingQuestion}
                isCreatingNew={isCreatingNew}
                examid={examid}
                section={section}
                currentGroupId={currentGroupId}
                onSave={fetchExamContent}
                questionIdToGroupMapping={QUESTION_ID_TO_GROUP}
                getSectionQuestionGroups={getSectionQuestionGroups}
            />

            <EditQuestionGroupDialog
                key={`group-${editingGroup?.groupid ?? 'none'}`}
                isOpen={editGroupOpen}
                onClose={() => setEditGroupOpen(false)}
                group={editingGroup}
                examid={examid}
                onSave={fetchExamContent}
            />
        </div>
    );
}

export default TopikExamSectionPage;
